/**
 * ProviderSelect — minimal <select> rendering the shared chat provider
 * options. Visual-styling-agnostic: callers pass their own className.
 *
 * Used by both the Andrei floating panel (terminal-themed) and the
 * JobWorkspace chat header (Metronic-styled) so the option list and
 * value grammar stay in lock-step.
 *
 * Unknown-value behaviour: if the current value isn't in the
 * built-in/fetched list (e.g. a stale localStorage entry pointing at
 * an OpenRouter model the user later removed), the select renders it
 * as a "(custom)" first option so the user can see + change it
 * instead of the dropdown silently snapping to the first item.
 */

import React from 'react';
import { useChatProviderOptions, withCustomValue } from './useChatProviderOptions';

export interface ProviderSelectProps {
  value: string;
  onChange: (next: string) => void;
  /** className passed straight to <select>. */
  className?: string;
  /** Optional title attribute for native tooltip. */
  title?: string;
  /** Useful when the select sits inside a draggable container —
   *  prevents the drag handler from intercepting clicks. */
  noDrag?: boolean;
  /** If supplied, called when the select renders. Used by AndreiChatPanel
   *  to set its own `title` attribute derived from the label. */
  onLabelResolved?: (label: string) => void;
}

export const ProviderSelect: React.FC<ProviderSelectProps> = ({
  value,
  onChange,
  className,
  title,
  noDrag,
  onLabelResolved,
}) => {
  const { options } = useChatProviderOptions();
  const rendered = withCustomValue(options, value);

  // Resolve label for the title attribute / parent callback.
  const currentLabel = rendered.find((o) => o.value === value)?.label || value;
  React.useEffect(() => {
    onLabelResolved?.(currentLabel);
  }, [currentLabel, onLabelResolved]);

  return (
    <select
      className={className}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      title={title || `Model: ${currentLabel}`}
      aria-label={`Chat model: ${currentLabel}`}
      {...(noDrag ? { 'data-no-drag': true } : {})}
    >
      {rendered.map((opt) => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  );
};

export default ProviderSelect;
